import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { createContext, ReactNode, useContext } from "react";

import { Experience } from "@/utils/experience";

type ExperienceContextValue = Partial<Experience> & {
  expandedMode: boolean;
  expandedModeSet: (expandedMode: boolean) => void;
  screenshotDesktop: boolean;
  screenshotDesktopSet: (screenshotDesktop: boolean) => void;
  screenshot: boolean;
  screenshotFullPage: boolean;
  screenshotFullPageSet: (screenshotFullPage: boolean) => void;
  screenshotMobile: boolean;
  screenshotMobileSet: (screenshotMobile: boolean) => void;
  screenshotSet: (screenshot: boolean) => void;
};

const ExperienceContext = createContext<ExperienceContextValue>({
  expandedMode: false,
  expandedModeSet: () => {},
  screenshotDesktop: false,
  screenshotDesktopSet: () => {},
  screenshot: false,
  screenshotFullPage: true,
  screenshotFullPageSet: () => {},
  screenshotMobile: false,
  screenshotMobileSet: () => {},
  screenshotSet: () => {}
});

export const ExperienceProvider: React.FC<{
  experience: Partial<Experience>;
  children: ReactNode;
}> = ({ experience, children }) => {
  const SCREENSHOT_DESKTOP_SEARCH_PARAM = "mdrna-screenshot-desktop";
  const SCREENSHOT_MOBILE_SEARCH_PARAM = "mdrna-screenshot-mobile";
  const EXPANDED_MODE_SEARCH_PARAM = "mdrna-expanded-mode";
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [expandedMode, expandedModeSet] = React.useState(
    !!searchParams.get(EXPANDED_MODE_SEARCH_PARAM)
  );
  const [screenshotDesktop, screenshotDesktopSetState] = React.useState(
    !!searchParams.get(SCREENSHOT_DESKTOP_SEARCH_PARAM)
  );
  const [screenshotFullPage, screenshotFullPageSet] = React.useState(true);
  const [screenshotMobile, screenshotMobileSetState] = React.useState(
    !!searchParams.get(SCREENSHOT_MOBILE_SEARCH_PARAM)
  );
  const screenshot = screenshotDesktop || screenshotMobile;

  React.useEffect(() => {
    const expandedModeNext = !!searchParams.get(EXPANDED_MODE_SEARCH_PARAM);

    if (expandedModeNext !== expandedMode) {
      expandedModeSet(expandedModeNext);
    }

    const screenshotDesktopNext = !!searchParams.get(SCREENSHOT_DESKTOP_SEARCH_PARAM);

    if (screenshotDesktopNext !== screenshotDesktop) {
      screenshotDesktopSetState(screenshotDesktopNext);
    }

    const screenshotMobileNext = !!searchParams.get(SCREENSHOT_MOBILE_SEARCH_PARAM);

    if (screenshotMobileNext !== screenshotMobile) {
      screenshotMobileSetState(screenshotMobileNext);
    }
  }, [
    expandedMode,
    EXPANDED_MODE_SEARCH_PARAM,
    screenshot,
    screenshotDesktop,
    screenshotMobile,
    searchParams,
    SCREENSHOT_DESKTOP_SEARCH_PARAM,
    SCREENSHOT_MOBILE_SEARCH_PARAM
  ]);

  const handleScreenshotSet = React.useCallback(
    (screenshotNext: boolean) => {
      if (screenshotNext) {
        return;
      }

      const params = new URLSearchParams(searchParams.toString());

      params.delete(SCREENSHOT_DESKTOP_SEARCH_PARAM);
      params.delete(SCREENSHOT_MOBILE_SEARCH_PARAM);

      const qs = params.toString();

      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [
      pathname,
      searchParams,
      router,
      SCREENSHOT_DESKTOP_SEARCH_PARAM,
      SCREENSHOT_MOBILE_SEARCH_PARAM
    ]
  );

  const handleExpandedModeSet = React.useCallback(
    (expandedModeNext: boolean) => {
      const params = new URLSearchParams(searchParams.toString());

      if (expandedModeNext) {
        params.set(EXPANDED_MODE_SEARCH_PARAM, "1");
      } else {
        params.delete(EXPANDED_MODE_SEARCH_PARAM);
      }

      const qs = params.toString();

      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, searchParams, router, EXPANDED_MODE_SEARCH_PARAM]
  );

  const handleScreenshotDesktopSet = React.useCallback(
    (screenshotDesktopNext: boolean) => {
      const params = new URLSearchParams(searchParams.toString());

      if (screenshotDesktopNext) {
        params.set(SCREENSHOT_DESKTOP_SEARCH_PARAM, "1");
        params.delete(SCREENSHOT_MOBILE_SEARCH_PARAM);
      } else {
        params.delete(SCREENSHOT_DESKTOP_SEARCH_PARAM);
      }

      const qs = params.toString();

      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [
      pathname,
      searchParams,
      router,
      SCREENSHOT_DESKTOP_SEARCH_PARAM,
      SCREENSHOT_MOBILE_SEARCH_PARAM
    ]
  );

  const handleScreenshotMobileSet = React.useCallback(
    (screenshotMobileNext: boolean) => {
      const params = new URLSearchParams(searchParams.toString());

      if (screenshotMobileNext) {
        params.set(SCREENSHOT_MOBILE_SEARCH_PARAM, "1");
        params.delete(SCREENSHOT_DESKTOP_SEARCH_PARAM);
      } else {
        params.delete(SCREENSHOT_MOBILE_SEARCH_PARAM);
      }

      const qs = params.toString();

      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [
      pathname,
      searchParams,
      router,
      SCREENSHOT_DESKTOP_SEARCH_PARAM,
      SCREENSHOT_MOBILE_SEARCH_PARAM
    ]
  );

  return (
    <ExperienceContext.Provider
      value={{
        ...experience,
        expandedMode,
        expandedModeSet: handleExpandedModeSet,
        screenshotDesktop,
        screenshotDesktopSet: handleScreenshotDesktopSet,
        screenshotFullPage,
        screenshotFullPageSet,
        screenshotMobile,
        screenshotMobileSet: handleScreenshotMobileSet,
        screenshotSet: handleScreenshotSet,
        screenshot
      }}
    >
      {children}
    </ExperienceContext.Provider>
  );
};

export const useExperience = () => {
  const context = useContext(ExperienceContext);

  if (!context) {
    throw new Error("useExperience must be used within an ExperienceProvider");
  }

  return context;
};
